import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import {
  Plus,
  Package,
  Loader2,
  Truck,
  Bike,
  UserPlus,
  ShieldCheck,
  ShieldAlert,
  Star,
  Clock,
  Box,
  Ruler,
  Warehouse,
  Zap,
  CalendarDays,
  Download,
  Ban,
  KeyRound,
  Camera,
} from "lucide-react";
import { cancelWindowFor, formatRemaining } from "../../utils/cancelWindow";
import StatusBadge from "../StatusBadge";
import PriceCard from "../PriceCard";
import HubPriceCard from "../HubPriceCard";
import { downloadInvoice } from "../../utils/invoiceDownload";
import {
  sizeLabels,
  sizeColors,
  serviceLabels,
  vehicleLabels,
  vehicleColors,
  nextActionLabel,
} from "../../utils/orderConstants";

/**
 * Orders table — read-only render. All actions invoked via callbacks.
 * Behavior preserved 1:1 from Orders.jsx (no logic changes).
 */
export function OrderList({
  orders,
  loading,
  role,
  updatingId,
  onBookFirst,
  onAcceptPickup,
  onStatusAdvance,
  onConfirmCod,
  onVerifyTarget,         // setVerifyTarget({ order, mode })
  onRate,                 // (trackingId) => void
  onReportDamage,         // (order) => void
  onOpenAssignDialog,     // ({ trackingId, b2bMode, b2bRole, crewMode, crewSize, vehicleType }) => void
  onCancelOrder,          // (order) => void  — admin only
  onOpenDetails,          // (trackingId) => void  — opens OrderDetailsDialog
  onProofAction,          // ({ order, kind }) => void  — Sprint 72 proofs
}) {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden animate-fadeInUp stagger-2" data-testid="orders-table">
        <div className="p-16 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00] mx-auto mb-3" />
          <p className="text-sm text-slate-400">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden animate-fadeInUp stagger-2" data-testid="orders-table">
        <div className="p-16 text-center" data-testid="no-orders">
          <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package className="h-8 w-8 text-[#FF6B00]/40" />
          </div>
          <p className="text-base font-semibold text-slate-700">No orders yet</p>
          <p className="text-sm text-slate-400 mt-1 max-w-xs mx-auto">
            {role === "user" ? "Book your first pickup and get it delivered in minutes!" : "No orders match your current filters."}
          </p>
          {role === "user" && (
            <Button onClick={onBookFirst} className="mt-4 bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl px-5 btn-press" data-testid="empty-book-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Book First Pickup
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden animate-fadeInUp stagger-2" data-testid="orders-table">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-100 bg-slate-50/60">
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6 py-3">Tracking</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Service</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Item</TableHead>
              {(role === "agent" || role === "admin") && (
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hidden md:table-cell py-3">AI Dims</TableHead>
              )}
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hidden md:table-cell py-3">Route</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Price</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hidden lg:table-cell py-3">ETA</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Pickup Agent</TableHead>
              {(role === "admin") && (
                <>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hidden lg:table-cell py-3">Origin Hub</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hidden lg:table-cell py-3">Dest Hub</TableHead>
                </>
              )}
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Delivery Agent</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Status</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Payment</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order, idx) => (
              <TableRow
                key={order.tracking_id}
                className="border-slate-50 row-hover animate-fadeInUp cursor-pointer"
                style={{ animationDelay: `${idx * 0.03}s` }}
                data-testid={`orders-row-${order.tracking_id}`}
                onClick={() => onOpenDetails && onOpenDetails(order.tracking_id)}
              >
                <TableCell className="pl-6 py-3.5">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/dashboard/orders/${order.tracking_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm font-mono font-semibold text-[#FF6B00] hover:underline"
                      data-testid={`orders-track-link-${order.tracking_id}`}
                    >
                      {order.tracking_id}
                    </Link>
                    {order.item_size && (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${sizeColors[order.item_size]}`}>
                        {sizeLabels[order.item_size]}
                      </span>
                    )}
                    {order.pickup_type === "scheduled" && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200" data-testid={`scheduled-badge-${order.tracking_id}`}>
                        <CalendarDays className="h-2.5 w-2.5" />
                        {order.scheduled_date}
                      </span>
                    )}
                  </div>
                  {(role === "admin" || role === "agent") && order.user_name && (
                    <div className="mt-1 space-y-0.5" data-testid={`customer-info-${order.tracking_id}`}>
                      <p className="text-[10px] text-slate-500 font-medium">{order.user_name}</p>
                      {order.user_phone && <p className="text-[10px] text-slate-400">{order.user_phone}</p>}
                    </div>
                  )}
                </TableCell>
                <TableCell className="py-3.5">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      {order.service_type !== "delivery" && <Truck className="h-3.5 w-3.5 text-[#FF6B00]" />}
                      <span className="text-xs font-semibold text-slate-700">{serviceLabels[order.service_type] || "Delivery"}</span>
                    </div>
                    {order.vehicle_type && (
                      <span className={`inline-flex items-center gap-1 w-fit px-2 py-0.5 rounded text-[10px] font-bold ${vehicleColors[order.vehicle_type] || vehicleColors.bike}`}>
                        {order.vehicle_type === "truck" ? <Truck className="h-2.5 w-2.5" /> : order.vehicle_type === "van" ? <Truck className="h-2.5 w-2.5" /> : <Bike className="h-2.5 w-2.5" />}
                        {vehicleLabels[order.vehicle_type] || "Bike"}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-3.5">
                  <span className="text-sm text-slate-700 font-medium">{order.package_description}</span>
                  {order.package_weight && <span className="text-[10px] text-slate-400 ml-1.5">{order.package_weight}kg</span>}
                </TableCell>
                {(role === "agent" || role === "admin") && (
                  <TableCell className="hidden md:table-cell py-3.5">
                    {order.ai_dimensions ? (
                      <div className="space-y-1.5" data-testid={`ai-dims-${order.tracking_id}`}>
                        <span className="inline-flex items-center gap-1 bg-[#FF6B00]/10 text-[#FF6B00] border border-[#FF6B00]/30 px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider" data-testid={`packing-info-badge-${order.tracking_id}`}>
                          <Package className="h-3 w-3" />
                          Packing Info
                        </span>
                        {order.ai_dimensions.dimensions_cm && (
                          <div className="flex items-center gap-1">
                            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded text-[10px] font-bold">
                              <Ruler className="h-2.5 w-2.5" />
                              {order.ai_dimensions.dimensions_cm}
                            </span>
                          </div>
                        )}
                        {order.ai_dimensions.recommended_size && (
                          <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded text-[10px] font-bold">
                            <Box className="h-2.5 w-2.5" />
                            {order.ai_dimensions.recommended_size} box
                          </span>
                        )}
                        {order.ai_dimensions.volume_cm3 && (
                          <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded text-[10px] font-bold">
                            {order.ai_dimensions.volume_cm3.toLocaleString()} cm³
                          </span>
                        )}
                        {order.ai_dimensions.item_detected && (
                          <p className="text-[10px] text-slate-600 font-medium truncate max-w-[160px]">{order.ai_dimensions.item_detected}</p>
                        )}
                        {order.ai_dimensions.packing_tips && (
                          <p className="text-[10px] text-[#FF6B00] font-semibold truncate max-w-[160px]">Tip: {order.ai_dimensions.packing_tips}</p>
                        )}
                        {order.ai_dimensions.items_estimate && (
                          <p className="text-[10px] text-slate-500 truncate max-w-[160px]">{order.ai_dimensions.items_estimate}</p>
                        )}
                        {order.ai_dimensions.crew_size && (
                          <span className="text-[10px] text-slate-400">{order.ai_dimensions.crew_size} crew</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-300">--</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="hidden md:table-cell py-3.5">
                  <div className="text-xs text-slate-500 max-w-[200px] space-y-0.5">
                    <div className="truncate">{order.pickup_address}</div>
                    <div className="truncate text-slate-400">{order.delivery_address}</div>
                    <div className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      order.delivery_mode === "hub_routed"
                        ? "bg-violet-50 text-violet-700 border border-violet-200"
                        : "bg-orange-50 text-[#FF6B00] border border-orange-200"
                    }`} data-testid={`delivery-mode-label-${order.tracking_id}`}>
                      {order.delivery_mode === "hub_routed" ? <Warehouse className="h-2.5 w-2.5" /> : <Zap className="h-2.5 w-2.5" />}
                      {order.delivery_mode === "hub_routed" ? "Hub Routed" : "Express"}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-3.5">
                  {order.delivery_mode === "hub_routed" && order.hub_pricing && order.service_type === "delivery" ? (
                    <HubPriceCard pricing={order.hub_pricing} compact isAdmin={role === "admin"} />
                  ) : order.price ? (
                    <PriceCard price={order.price} compact />
                  ) : (
                    <span className="text-xs text-slate-400">--</span>
                  )}
                  {(() => {
                    // Locked courier badge — read frozen fields straight from
                    // the order doc. Falls back to the legacy nested
                    // `selected_courier` dict for orders booked before the flat
                    // fields were introduced. Stays hidden when neither is set.
                    const courierName = order.selected_courier_name
                      || order.selected_courier?.courier_name
                      || order.selected_courier?.name;
                    const courierEta = order.selected_courier_eta ?? order.selected_courier?.estimated_days;
                    const courierProvider = order.selected_courier_provider || order.selected_courier?.courier_id;
                    if (!courierName) return null;
                    return (
                      <div
                        className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-orange-50 text-[#FF6B00] border border-orange-200"
                        data-testid={`selected-courier-${order.tracking_id}`}
                        title={courierProvider ? `Provider: ${courierProvider}` : courierName}
                      >
                        <Truck className="h-2.5 w-2.5" />
                        via {courierName}
                        {courierEta ? <span className="text-slate-500 font-normal">· {courierEta}d</span> : null}
                      </div>
                    );
                  })()}
                  {(() => {
                    // Sprint 55 — pending-settlement / refund summary chips.
                    // Sum unsettled amounts so admin/customer see the live
                    // balance owed/owing without expanding the order.
                    const pendingAdd = (order.pending_settlements || [])
                      .filter((p) => p?.status === "pending")
                      .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
                    const pendingRefund = (order.pending_refunds || [])
                      .filter((p) => p?.status === "pending")
                      .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
                    if (!pendingAdd && !pendingRefund) return null;
                    return (
                      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`pending-money-${order.tracking_id}`}>
                        {pendingAdd > 0 && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                            data-testid={`pending-additional-${order.tracking_id}`}
                            title="Customer owes this amount due to a price-increasing edit"
                          >
                            ₹{pendingAdd} additional pending
                          </span>
                        )}
                        {pendingRefund > 0 && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200"
                            data-testid={`pending-refund-${order.tracking_id}`}
                            title="Refund pending — admin processes manually"
                          >
                            ₹{pendingRefund} refund pending
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {(() => {
                    // Sprint 56 — multi-parcel count chip. Hidden for
                    // single-parcel orders so the row stays compact.
                    const itemsCount = order.items_count
                      || (order.items || []).length
                      || (order.price?.items_count || 0);
                    if (itemsCount <= 1) return null;
                    return (
                      <span
                        className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200"
                        data-testid={`multi-items-${order.tracking_id}`}
                        title="This order has multiple parcels"
                      >
                        <Package className="h-2.5 w-2.5" />
                        {itemsCount} parcels
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="hidden lg:table-cell py-3.5">
                  {(order.price?.zone || order.price?.zone_info?.zone) && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                      (() => {
                        const z = order.price.zone || order.price.zone_info?.zone || "";
                        if (z === "A" || z === "short") return "bg-emerald-50 text-emerald-700 border-emerald-200";
                        if (z === "B" || z === "medium") return "bg-blue-50 text-blue-700 border-blue-200";
                        if (z === "C" || z === "long") return "bg-amber-50 text-amber-700 border-amber-200";
                        return "bg-red-50 text-red-700 border-red-200";
                      })()
                    }`} data-testid={`zone-${order.tracking_id}`}>
                      {order.price.zone
                        ? `Zone ${order.price.zone}`
                        : (order.price.zone_info?.label || `Zone ${order.price.zone_info?.zone}`)}
                    </span>
                  )}
                  {(order.price?.estimated_days || order.price?.estimated_delivery_days) && order.status !== "delivered" ? (
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-md text-[10px] font-semibold" data-testid={`eta-${order.tracking_id}`}>
                      <Clock className="h-2.5 w-2.5" />
                      ~{(() => {
                        const d = order.price.estimated_days || order.price.estimated_delivery_days;
                        return d === 1 ? "1 day" : `${d} days`;
                      })()}
                    </span>
                  ) : order.status === "delivered" ? (
                    <span className="text-[10px] text-green-600 font-medium">Delivered</span>
                  ) : (
                    <span className="text-xs text-slate-400">--</span>
                  )}
                </TableCell>
                {/* Pickup Agent / Crew */}
                <TableCell className="py-3.5">
                  {order.assigned_crew && order.assigned_crew.length > 0 ? (
                    <div className="space-y-0.5" data-testid={`crew-${order.tracking_id}`}>
                      {order.assigned_crew.map((c, i) => (
                        <div key={c.agent_id || i}>
                          <span className="text-xs text-blue-700 font-medium block">{c.name}</span>
                          {c.phone && <span className="text-[10px] text-slate-400 block">{c.phone}</span>}
                        </div>
                      ))}
                      <span className="text-[10px] text-slate-400">{order.assigned_crew.length} crew</span>
                    </div>
                  ) : order.delivery_mode === "hub_routed" ? (
                    order.pickup_agent_name
                      ? <div data-testid={`pickup-agent-${order.tracking_id}`}>
                          <span className="text-xs text-blue-700 font-medium block">{order.pickup_agent_name}</span>
                          {order.pickup_agent_phone && <span className="text-[10px] text-slate-400 block">{order.pickup_agent_phone}</span>}
                        </div>
                      : <span className="text-[10px] text-slate-400 italic">Unassigned</span>
                  ) : order.agent_name
                    ? <div>
                        <span className="text-xs text-slate-700 font-medium block">{order.agent_name}</span>
                        {order.agent_phone && <span className="text-[10px] text-slate-400 block">{order.agent_phone}</span>}
                      </div>
                    : <span className="text-[10px] text-slate-400 italic">Unassigned</span>
                  }
                </TableCell>
                {/* Origin Hub & Dest Hub — Admin only */}
                {(role === "admin") && (
                  <>
                    <TableCell className="hidden lg:table-cell py-3.5">
                      {order.delivery_mode === "hub_routed" && order.hub_pair?.origin_hub ? (
                        <div className="flex flex-col gap-0.5" data-testid={`origin-hub-${order.tracking_id}`}>
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700">
                            <Warehouse className="h-2.5 w-2.5" />
                            {order.hub_pair.origin_hub.name || order.hub_pair.origin_hub.hub_id}
                          </span>
                          {order.hub_pair.origin_hub.city && (
                            <span className="text-[10px] text-slate-400">{order.hub_pair.origin_hub.city}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-300">--</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell py-3.5">
                      {order.delivery_mode === "hub_routed" && order.hub_pair?.destination_hub ? (
                        <div className="flex flex-col gap-0.5" data-testid={`dest-hub-${order.tracking_id}`}>
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-teal-700">
                            <Warehouse className="h-2.5 w-2.5" />
                            {order.hub_pair.destination_hub.name || order.hub_pair.destination_hub.hub_id}
                          </span>
                          {order.hub_pair.destination_hub.city && (
                            <span className="text-[10px] text-slate-400">{order.hub_pair.destination_hub.city}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-300">--</span>
                      )}
                    </TableCell>
                  </>
                )}
                {/* Delivery Agent */}
                <TableCell className="py-3.5">
                  {order.delivery_mode === "hub_routed" ? (
                    order.delivery_agent_name
                      ? <div data-testid={`delivery-agent-${order.tracking_id}`}>
                          <span className="text-xs text-emerald-700 font-medium block">{order.delivery_agent_name}</span>
                          {order.delivery_agent_phone && <span className="text-[10px] text-slate-400 block">{order.delivery_agent_phone}</span>}
                        </div>
                      : <span className="text-[10px] text-slate-400 italic">Unassigned</span>
                  ) : (
                    <span className="text-[10px] text-slate-300">--</span>
                  )}
                </TableCell>
                <TableCell className="py-3.5">
                  <StatusBadge status={order.status} />
                </TableCell>
                {/* Payment Mode */}
                <TableCell className="py-3.5" onClick={(e) => e.stopPropagation()}>
                  <div className="space-y-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border ${
                      order.payment_method === "cod" ? "bg-amber-50 text-amber-700 border-amber-200"
                      : order.payment_method === "wallet" ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                      : "bg-blue-50 text-blue-700 border-blue-200"
                    }`}>
                      {order.payment_method === "cod" ? "COD" : order.payment_method === "wallet" ? "Wallet" : "Online"}
                    </span>
                    {order.payment_status === "paid" ? (
                      <span className="block text-[9px] text-emerald-600 font-semibold">Paid</span>
                    ) : order.payment_method === "cod" && (role === "agent" || role === "admin") ? (
                      <Button
                        size="sm"
                        onClick={() => onConfirmCod(order.tracking_id)}
                        disabled={updatingId === order.tracking_id}
                        className="h-6 px-2 text-[9px] rounded-md bg-emerald-600 text-white hover:bg-emerald-700 font-semibold"
                        data-testid={`confirm-cod-${order.tracking_id}`}
                      >
                        {updatingId === order.tracking_id ? "..." : "Collect COD"}
                      </Button>
                    ) : (
                      <span className="block text-[9px] text-amber-600 font-semibold">Pending</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-3.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    {/* Agent: Accept Pickup for pending orders */}
                    {role === "agent" && order.status === "pending" && !order.agent_id && (
                      <Button
                        size="sm"
                        onClick={() => onAcceptPickup(order.tracking_id)}
                        disabled={updatingId === order.tracking_id}
                        className="rounded-lg px-3 py-1 text-xs font-semibold h-8 btn-press bg-[#FF6B00] text-white hover:bg-[#E65A00]"
                        data-testid={`accept-pickup-${order.tracking_id}`}
                      >
                        {updatingId === order.tracking_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3 mr-1" />}
                        Accept Pickup
                      </Button>
                    )}
                    {/* Agent: R7 Verify Weight (at pickup) */}
                    {role === "agent" && (order.status === "pickup_assigned" || order.status === "assigned" || order.status === "picked_up") && !order.pickup_verification && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onVerifyTarget({ order, mode: "weight" })}
                        className="rounded-lg px-3 py-1 text-xs font-semibold h-8 border-orange-200 text-[#FF6B00] hover:bg-orange-50"
                        data-testid={`verify-weight-${order.tracking_id}`}
                      >
                        <Ruler className="h-3 w-3 mr-1" />
                        Verify Weight
                      </Button>
                    )}
                    {/* Sprint 72 — Pickup OTP: agent must enter customer's OTP before picked_up */}
                    {role === "agent" && (order.status === "pickup_assigned" || order.status === "assigned") && !order.pickup_otp_verified_at && (
                      <Button
                        size="sm"
                        onClick={() => onProofAction && onProofAction({ order, kind: "pickup_otp" })}
                        className="rounded-lg px-3 py-1 text-xs font-semibold h-8 bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid={`verify-pickup-otp-${order.tracking_id}`}
                      >
                        <KeyRound className="h-3 w-3 mr-1" /> Verify Pickup OTP
                      </Button>
                    )}
                    {/* Sprint 72 — Handover photo at origin hub */}
                    {role === "agent" && order.status === "picked_up" && order.delivery_mode === "hub_routed" && !order.origin_hub_handover_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onProofAction && onProofAction({ order, kind: "pickup_handover" })}
                        className="rounded-lg px-3 py-1 text-xs font-semibold h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`upload-handover-${order.tracking_id}`}
                      >
                        <Camera className="h-3 w-3 mr-1" /> Upload Handover Photo
                      </Button>
                    )}
                    {/* Agent: R8 Branch Drop-off (after pickup, before in-transit) */}
                    {role === "agent" && order.status === "picked_up" && !order.branch_drop && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onVerifyTarget({ order, mode: "drop" })}
                        className="rounded-lg px-3 py-1 text-xs font-semibold h-8 border-violet-200 text-violet-700 hover:bg-violet-50"
                        data-testid={`branch-drop-${order.tracking_id}`}
                      >
                        <Warehouse className="h-3 w-3 mr-1" />
                        Drop at Hub
                      </Button>
                    )}
                    {/* Agent action buttons — restricted for hub_routed linehaul stages */}
                    {role === "agent" && order.status !== "delivered" && nextActionLabel[order.status] && !(
                      order.delivery_mode === "hub_routed" && (order.status === "at_origin_hub" || order.status === "in_linehaul")
                    ) && (
                      <Button
                        size="sm"
                        onClick={() => onStatusAdvance(order.tracking_id, order.status, order.order_type)}
                        disabled={updatingId === order.tracking_id}
                        className={`rounded-lg px-3 py-1 text-xs font-semibold h-8 btn-press ${
                          order.status === "in_transit" || order.status === "out_for_delivery"
                            ? "bg-green-600 text-white hover:bg-green-700"
                            : order.status === "at_dest_hub"
                              ? "bg-violet-600 text-white hover:bg-violet-700"
                              : "bg-[#FF6B00] text-white hover:bg-[#E65A00]"
                        }`}
                        data-testid={`advance-status-${order.tracking_id}`}
                      >
                        {updatingId === order.tracking_id ? <Loader2 className="h-3 w-3 animate-spin" /> : (
                          (order.status === "in_transit" || order.status === "out_for_delivery") ? <ShieldCheck className="h-3 w-3 mr-1" /> :
                          order.status === "at_dest_hub" ? <Warehouse className="h-3 w-3 mr-1" /> :
                          <Truck className="h-3 w-3 mr-1" />
                        )}
                        {nextActionLabel[order.status]}
                      </Button>
                    )}
                    {role === "user" && order.status === "delivered" && !order.rating && (
                      <Button
                        size="sm"
                        onClick={() => onRate(order.tracking_id)}
                        className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 rounded-lg px-3 py-1 text-xs font-semibold h-8 btn-press"
                        data-testid={`rate-order-${order.tracking_id}`}
                      >
                        <Star className="h-3 w-3 mr-1" />
                        Rate
                      </Button>
                    )}
                    {role === "user" && order.rating && (
                      <span className="inline-flex items-center gap-0.5 text-xs text-amber-600 font-semibold">
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                        {order.rating.score}/5
                      </span>
                    )}
                    {/* B2B Assign Agent with Vehicle */}
                    {role === "admin" && order.order_type === "b2b_express" && (
                      (order.status === "pending" && !order.pickup_agent_name) ||
                      (order.status === "at_dest_hub" && !order.delivery_agent_name)
                    ) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onOpenAssignDialog({
                          trackingId: order.tracking_id,
                          b2bMode: true,
                          b2bRole: order.status === "at_dest_hub" ? "delivery" : "pickup",
                          crewMode: false,
                          crewSize: 1,
                          vehicleType: "van",
                        })}
                        className="rounded-lg px-3 py-1 text-xs font-semibold h-8 border-orange-200 text-[#FF6B00] hover:border-[#FF6B00] hover:bg-orange-50 transition-colors"
                        data-testid={`b2b-assign-${order.tracking_id}`}
                      >
                        <Truck className="h-3 w-3 mr-1" />
                        {order.status === "at_dest_hub" ? "Assign Delivery" : "Assign + Vehicle"}
                      </Button>
                    )}
                    {role === "admin" && order.order_type !== "b2b_express" && (
                      (order.service_type && order.service_type !== "delivery" && order.status === "pending" && (!order.assigned_crew || order.assigned_crew.length < (order.price?.crew_size || 1))) ||
                      (order.delivery_mode === "hub_routed"
                        ? (order.status === "pending" && !order.pickup_agent_name) || (order.status === "at_dest_hub" && !order.delivery_agent_name)
                        : order.service_type === "delivery" && !order.agent_name
                      )
                    ) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const isCrewService = order.service_type && order.service_type !== "delivery";
                          const needed = order.price?.crew_size || 1;
                          onOpenAssignDialog({
                            trackingId: order.tracking_id,
                            b2bMode: false,
                            b2bRole: "pickup",
                            crewMode: isCrewService && needed > 1,
                            crewSize: needed,
                            vehicleType: "van",
                          });
                        }}
                        className="rounded-lg px-3 py-1 text-xs font-semibold h-8 border-slate-200 hover:border-blue-400 hover:text-blue-600 transition-colors"
                        data-testid={`assign-agent-${order.tracking_id}`}
                      >
                        <UserPlus className="h-3 w-3 mr-1" />
                        {order.service_type && order.service_type !== "delivery"
                          ? `Assign Crew (${order.price?.crew_size || 1})`
                          : order.delivery_mode === "hub_routed" && order.status === "at_dest_hub"
                            ? "Assign Delivery"
                            : "Assign"}
                      </Button>
                    )}
                    {role === "admin" && order.status !== "delivered" && order.status !== "return_delivered" && nextActionLabel[order.status] && (
                      <Button
                        size="sm"
                        onClick={() => onStatusAdvance(order.tracking_id, order.status, order.order_type)}
                        disabled={updatingId === order.tracking_id}
                        className={`rounded-lg px-3 py-1 text-xs font-semibold h-8 btn-press ${
                          order.status === "in_transit" || order.status === "out_for_delivery"
                            ? "bg-green-600 text-white hover:bg-green-700"
                            : order.order_type === "b2b_return"
                              ? "bg-red-600 text-white hover:bg-red-700"
                              : order.order_type === "b2b_express"
                                ? "bg-[#FF6B00] text-white hover:bg-[#E65A00]"
                                : "bg-slate-800 text-white hover:bg-slate-700"
                        }`}
                        data-testid={`admin-advance-${order.tracking_id}`}
                      >
                        {updatingId === order.tracking_id ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        {nextActionLabel[order.status]}
                      </Button>
                    )}
                    {role === "admin" && order.status !== "delivered" && order.status !== "cancelled" && order.status !== "return_delivered" && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onCancelOrder(order); }}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold h-8 border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors"
                        data-testid={`admin-cancel-${order.tracking_id}`}
                        title="Cancel this order with a reason"
                      >
                        <Ban className="h-3 w-3" /> Cancel
                      </button>
                    )}
                    {role === "user" && order.status !== "delivered" && order.status !== "cancelled" && order.status !== "return_delivered" && (() => {
                      const win = cancelWindowFor(order);
                      const tooltip = win.can_cancel
                        ? (win.accepted ? `${formatRemaining(win.ms_remaining)} to cancel` : "You can cancel anytime before pickup")
                        : (win.reason || "Cancellation not available");
                      return (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); if (win.can_cancel) onCancelOrder(order); }}
                          disabled={!win.can_cancel}
                          className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold h-8 border transition-colors ${
                            win.can_cancel
                              ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                              : "border-slate-200 text-slate-400 cursor-not-allowed"
                          }`}
                          data-testid={`customer-cancel-${order.tracking_id}`}
                          title={tooltip}
                        >
                          <Ban className="h-3 w-3" />
                          {win.accepted && win.can_cancel ? formatRemaining(win.ms_remaining) : "Cancel"}
                        </button>
                      );
                    })()}
                    {/* Universal Invoice Download — available for everyone */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); downloadInvoice(order.tracking_id); }}
                      className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold h-8 border border-slate-200 text-slate-600 hover:border-[#FF6B00] hover:text-[#FF6B00] hover:bg-orange-50 transition-colors"
                      data-testid={`download-invoice-${order.tracking_id}`}
                      title="Download Invoice PDF"
                    >
                      <Download className="h-3 w-3" />
                      Invoice
                    </button>
                    {role === "user" && order.status === "delivered" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onReportDamage(order); }}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold h-8 border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                        data-testid={`report-damage-${order.tracking_id}`}
                        title="Report damage or file a claim"
                      >
                        <ShieldAlert className="h-3 w-3" /> Report Damage
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default OrderList;
